import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorText, LoadingView, PrimaryButton, SBCard, SecondaryButton } from "@/components/ui";
import { appDeepLinkPath } from "@/features/ai/services/appNavigation";
import { ESPNAccessRequestActionSection } from "@/features/admin/ESPNAccessRequestAdminView";
import {
  partitionUserNotifications,
  userNotificationRoute,
  type UserNotification,
  type UserNotificationKind,
} from "@/models/userNotification";
import {
  deleteNotification,
  fetchNotification,
  listNotifications,
  markNotificationRead,
} from "@/services/notifications";
import { usePushNotifications } from "./PushNotificationProvider";
import "./notifications.css";

export function NotificationListView() {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!currentUser) return;
    setIsLoading(true);
    setError(null);
    try {
      setNotifications(await listNotifications(currentUser.id));
    } catch (cause) {
      setError(errorMessage(cause, "Notifications could not be loaded."));
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const remove = async (notification: UserNotification) => {
    setError(null);
    try {
      await deleteNotification(notification.id);
      setNotifications((current) => current.filter((item) => item.id !== notification.id));
    } catch (cause) {
      setError(errorMessage(cause, "The notification could not be deleted."));
    }
  };

  const partitioned = useMemo(
    () => partitionUserNotifications(notifications),
    [notifications],
  );

  return (
    <div className="list-screen notification-screen">
      <div className="list-screen-head notification-screen-head">
        <div>
          <p className="home-eyebrow">Account</p>
          <h1>Notifications</h1>
          <p className="home-hint">Updates about requests, support conversations, and account activity.</p>
        </div>
        <SecondaryButton title={isLoading ? "Refreshing…" : "Refresh"} onClick={() => void load()} disabled={isLoading} />
      </div>

      <PushPermissionCard />
      <ErrorText>{error}</ErrorText>

      {isLoading && notifications.length === 0 ? (
        <LoadingView label="Loading notifications…" />
      ) : notifications.length === 0 ? (
        <SBCard className="notification-empty">
          <span className="notification-empty-icon" aria-hidden>🔕</span>
          <h2>You&rsquo;re all caught up</h2>
          <p className="home-hint">Notifications about your requests and account will appear here.</p>
        </SBCard>
      ) : (
        <div className="notification-sections">
          {partitioned.unread.length > 0 && (
            <NotificationSection title="Unread" notifications={partitioned.unread} onDelete={remove} />
          )}
          {partitioned.earlier.length > 0 && (
            <NotificationSection
              title={partitioned.unread.length > 0 ? "Earlier" : "Recent"}
              notifications={partitioned.earlier}
              onDelete={remove}
            />
          )}
        </div>
      )}
    </div>
  );
}

function NotificationSection({
  title,
  notifications,
  onDelete,
}: {
  title: string;
  notifications: UserNotification[];
  onDelete: (notification: UserNotification) => Promise<void>;
}) {
  return (
    <section className="notification-section">
      <h2>{title}</h2>
      <SBCard className="notification-list-card">
        {notifications.map((notification) => (
          <div className="notification-row-wrap" key={notification.id}>
            <Link className="notification-row" to={`/account/notifications/${encodeURIComponent(notification.id)}`}>
              <span className="notification-kind-icon" aria-hidden>
                {notificationKindIcon(notification.kind)}
              </span>
              <span className="notification-row-copy">
                <strong>{notification.title}</strong>
                <span>{notification.body}</span>
                <time dateTime={notification.createdAt.toISOString()}>
                  {relativeDate(notification.createdAt)}
                </time>
              </span>
              {notification.readAt === null && <span className="notification-unread-dot" aria-label="Unread" />}
              <span className="notification-chevron" aria-hidden>›</span>
            </Link>
            <button
              type="button"
              className="notification-row-delete"
              aria-label={`Delete ${notification.title}`}
              onClick={() => void onDelete(notification)}
            >
              Delete
            </button>
          </div>
        ))}
      </SBCard>
    </section>
  );
}

export function NotificationDetailView() {
  const { notificationID = "" } = useParams();
  const navigate = useNavigate();
  const { isSBotLabsAdmin } = useAuth();
  const [notification, setNotification] = useState<UserNotification | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const load = async () => {
      try {
        const result = await fetchNotification(notificationID);
        if (cancelled) return;
        setNotification(result);
        if (!result) {
          setError("This notification may have been deleted or expired.");
          return;
        }
        if (result.readAt === null) {
          try {
            await markNotificationRead(result.id);
            if (!cancelled) {
              setNotification((current) => current ? { ...current, readAt: new Date() } : current);
            }
          } catch {
            // Leave it unread locally; opening it later retries the server update.
          }
        }
      } catch (cause) {
        if (!cancelled) setError(errorMessage(cause, "The notification could not be loaded."));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [notificationID]);

  const remove = async () => {
    if (!notification) return;
    setError(null);
    try {
      await deleteNotification(notification.id);
      navigate("/account/notifications", { replace: true });
    } catch (cause) {
      setError(errorMessage(cause, "The notification could not be deleted."));
    }
  };

  const route = notification ? userNotificationRoute(notification) : null;
  const showESPNAction =
    notification?.kind.type === "known"
    && notification.kind.value === "espnAccessRequestSubmitted"
    && isSBotLabsAdmin
    && Boolean(notification.resourceID);

  if (isLoading) {
    return <LoadingView label="Loading notification…" />;
  }

  if (!notification) {
    return (
      <div className="list-screen notification-screen">
        <SBCard className="notification-unavailable">
          <span className="notification-empty-icon" aria-hidden>🔕</span>
          <h1>Notification unavailable</h1>
          <ErrorText>{error}</ErrorText>
          <Link to="/account/notifications">Back to Notifications</Link>
        </SBCard>
      </div>
    );
  }

  return (
    <div className="list-screen notification-screen">
      <div className="list-screen-head">
        <div>
          <p className="home-eyebrow">Notification</p>
          <h1>{notification.title}</h1>
        </div>
        <Link to="/account/notifications">Back to Notifications</Link>
      </div>

      <SBCard className="notification-detail-header">
        <span className="notification-kind-icon notification-kind-icon-large" aria-hidden>
          {notificationKindIcon(notification.kind)}
        </span>
        <div>
          <strong>{notification.title}</strong>
          <time dateTime={notification.createdAt.toISOString()}>
            {formatDateTime(notification.createdAt)} · {relativeDate(notification.createdAt)}
          </time>
        </div>
      </SBCard>

      <SBCard className="notification-detail-body">
        <p>{notification.body}</p>
      </SBCard>

      {showESPNAction ? (
        <ESPNAccessRequestActionSection requestID={notification.resourceID!} />
      ) : route && route.destination !== "notificationDetail" ? (
        <PrimaryButton
          title={`Open ${destinationTitle(route.destination)}`}
          onClick={() => navigate(appDeepLinkPath(route))}
        />
      ) : null}

      <ErrorText>{error}</ErrorText>
      <SecondaryButton title="Delete notification" destructive onClick={() => void remove()} />
    </div>
  );
}

function PushPermissionCard() {
  const {
    permissionStatus,
    configurationStatus,
    isEnabling,
    error,
    enablePush,
  } = usePushNotifications();

  if (permissionStatus === "granted" && configurationStatus !== "unavailable" && !error) {
    return null;
  }

  if (permissionStatus === "granted") {
    return (
      <SBCard className="notification-permission-card notification-permission-warning">
        <div>
          <h2>Browser alerts unavailable</h2>
          <p className="home-hint">
            SBot could not finish configuring browser alerts. New notifications still appear here.
          </p>
          <ErrorText>{error}</ErrorText>
        </div>
      </SBCard>
    );
  }

  if (permissionStatus === "default") {
    return (
      <SBCard className="notification-permission-card">
        <div>
          <h2>Get browser alerts</h2>
          <p className="home-hint">Enable push notifications to hear about replies and account updates when SBot is not open.</p>
          <ErrorText>{error}</ErrorText>
        </div>
        <PrimaryButton
          title={isEnabling ? "Enabling…" : "Enable push notifications"}
          disabled={isEnabling}
          onClick={() => void enablePush()}
        />
      </SBCard>
    );
  }

  const copy = permissionStatus === "unsupported"
    ? "This browser does not support SBot push notifications. New notifications still appear here."
    : "Browser alerts are blocked for this site. New notifications still appear here; use your browser's site settings to enable alerts.";

  return (
    <SBCard className="notification-permission-card notification-permission-warning">
      <div>
        <h2>{permissionStatus === "unsupported" ? "Browser alerts unavailable" : "Browser alerts are off"}</h2>
        <p className="home-hint">{copy}</p>
        <ErrorText>{error}</ErrorText>
      </div>
    </SBCard>
  );
}

function notificationKindIcon(kind: UserNotificationKind): string {
  if (kind.type === "unknown") return "🔔";
  switch (kind.value) {
    case "espnAccessRequestSubmitted":
    case "espnAccessRequestDecided":
      return "🏟️";
    case "supportRequestCreated":
    case "supportRequestReply":
      return "💬";
    case "supportAccessRequested":
    case "supportAccessGrantDecided":
    case "supportAccessGrantEnded":
      return "🔐";
    case "leagueBillingInvoice":
      return "💳";
  }
}

function destinationTitle(destination: string): string {
  const titles: Record<string, string> = {
    home: "Home",
    requestSupport: "Request Support",
    supportInbox: "Support Inbox",
    espnAccessRequests: "ESPN Access Requests",
    notifications: "Notifications",
    admin: "SBot Labs Admin",
  };
  return titles[destination] ?? "destination";
}

function formatDateTime(date: Date): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function relativeDate(date: Date, now = new Date()): string {
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absoluteSeconds = Math.abs(seconds);
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (absoluteSeconds < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  const hours = Math.round(minutes / 60);
  if (Math.abs(hours) < 24) return formatter.format(hours, "hour");
  const days = Math.round(hours / 24);
  if (Math.abs(days) < 30) return formatter.format(days, "day");
  return formatDateTime(date);
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}
